import { KLAVIYO_PUBLIC_KEY, KLAVIYO_LIST_ID } from './constants.js';

export function decodeJwtPayload(token) {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return {};
    const base64Url = parts[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    let binaryString;
    if (typeof atob === 'function') {
      binaryString = atob(base64);
    } else {
      binaryString = Buffer.from(base64, 'base64').toString('binary');
    }
    const jsonPayload = decodeURIComponent(
      binaryString
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (e) {
    console.error("Failed to decode JWT:", e);
    return {};
  }
}

export async function getQuintypeSession(request, origin, env) {
  if (!request) return null;

  // Adapt to either Next.js req object or standard Fetch Request object
  let cookieHeader = "";
  if (request.headers) {
    if (typeof request.headers.get === "function") {
      cookieHeader = request.headers.get("Cookie") || "";
    } else {
      cookieHeader = request.headers.cookie || "";
    }
  }

  const integrationId = env.INTEGRATION_ID || "4155";

  try {
    const res = await fetch(
      `${origin}/api/auth/v1/access-token/integrations/${integrationId}`,
      { method: "GET", headers: { "Content-Type": "application/json", Cookie: cookieHeader } }
    );
    if (res.status === 401 || res.status === 403 || !res.ok) return null;

    // fetch Response headers are case-insensitive and accessed via .get()
    const token = res.headers.get ? res.headers.get("x-integration-token") : res.headers["x-integration-token"];
    if (!token) return null;

    const decoded = decodeJwtPayload(token);
    if (!decoded.id) return null;

    return {
      userId: decoded.id,
      email: decoded.email || null,
      userName: decoded.name || decoded.email || "User",
      avatarUrl: decoded["avatar-url"] || null,
    };
  } catch (e) {
    console.error("getQuintypeSession error:", e);
    return null;
  }
}

// A safe local storage store
export const store = {
  get: (key) => {
    if (typeof window === 'undefined') return null;
    try {
      return localStorage.getItem(key);
    } catch (e) {
      return null;
    }
  },
  set: (key, value) => {
    if (typeof window === 'undefined') return;
    try {
      localStorage.setItem(key, value);
    } catch (e) {}
  },
  remove: (key) => {
    if (typeof window === 'undefined') return;
    try {
      localStorage.removeItem(key);
    } catch (e) {}
  }
};

export const clearSessionData = () => {
  store.remove("user_email");
  store.remove("fi_was_signed_in");
  store.remove("fi_was_website_signed_in");
  store.remove("fi_story_id");
  store.remove("fi_poll_choice");

  if (typeof document !== "undefined") {
    // Clear global member cookie
    document.cookie = "klaviyo_member=; path=/; expires=Thu, 01 Jan 1970 00:00:00 UTC;";
    
    // Clear any entity follow cookies
    const cookies = document.cookie.split(";");
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i].trim();
      if (cookie.startsWith("klaviyo_followed_")) {
        const eqIdx = cookie.indexOf("=");
        const name = eqIdx > -1 ? cookie.substr(0, eqIdx) : cookie;
        document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 UTC;`;
      }
    }
  }
};

export async function syncFromKlaviyo() {
  try {
    // 1. Try to fetch the user's active session from the browser client-side
    let email = null;
    let isWebsiteSignedIn = false;
    try {
      const meRes = await fetch("/api/auth/v1/users/me");
      if (meRes.ok) {
        const meData = await meRes.json();
        const resolvedEmail = meData.user?.email || meData.email || null;
        if (resolvedEmail) {
          email = resolvedEmail;
          isWebsiteSignedIn = true;
        }
      }
    } catch (err) {
      console.warn("[syncFromKlaviyo] Failed to fetch users/me:", err);
    }

    // 2. If not signed in to the site, check if they have a saved email in local storage
    if (!isWebsiteSignedIn) {
      const storedEmail = store.get("user_email");
      if (storedEmail) {
        email = storedEmail;
      }
    }

    // 3. If they were previously signed in to the site, but now aren't: clear session data (logout detection)
    if (isWebsiteSignedIn === false && store.get("fi_was_website_signed_in") === "true") {
      clearSessionData();
      return { isSignedIn: false, email: null };
    }

    // 4. If we have an email, query Klaviyo using the email parameter via the proxy
    if (email) {
      if (isWebsiteSignedIn) {
        store.set("fi_was_website_signed_in", "true");
      }
      store.set("user_email", email);

      const profileRes = await fetch(`/api/klaviyo-profile-test?email=${encodeURIComponent(email)}`, {
        credentials: "include",
      });

      if (profileRes.ok) {
        const profileData = await profileRes.json();
        if (profileData.status === "ok") {
          return {
            isSignedIn: true,
            email: profileData.email || email,
            publishers: profileData.publishers || [],
            followedEntities: profileData.user_following_entities || [],
          };
        }
      }
    }

    return { isSignedIn: false, email: null };
  } catch (e) {
    console.error("[FI] Sync failed:", e);
    return { isSignedIn: false, email: null };
  }
}

export function getCurrentPublisherName() {
  if (typeof window === "undefined") return "Football Insider";
  const hostname = window.location.hostname;
  if (hostname.includes("theportsmouthnews") || hostname.includes("portsmouth")) return "The Portsmouth News";
  if (hostname.includes("mackem")) return "Mackem News";
  if (hostname.includes("liverpoolnews")) return "Liverpool News 247";
  if (hostname.includes("astonvillanews")) return "Aston Villa News";
  if (hostname.includes("tottenhamhotspurnews")) return "Tottenham Hotspur News";
  if (hostname.includes("westbromnews")) return "West Brom News";
  if (hostname.includes("motleedsnews")) return "MOT Leeds News";
  if (hostname.includes("westhamzone")) return "West Ham Zone";
  if (hostname.includes("goodisonnews")) return "Goodison News";
  if (hostname.includes("ibroxnews")) return "Ibrox News";
  return "Football Insider";
}

export async function subscribeEntityToKlaviyo(email, entity) {
  // Save email in localStorage to persist identity for guest sessions
  store.set("user_email", email);
  const PUBLISHER_NAME = getCurrentPublisherName();

  let remoteEntities = [];
  let existingPublishers = [];
  let existingSourceBrand = null;

  try {
    const profileRes = await fetch(
      "/api/klaviyo-profile-test?email=" + encodeURIComponent(email),
      { credentials: "include" }
    );
    if (profileRes.ok) {
      const profileData = await profileRes.json();
      if (Array.isArray(profileData.user_following_entities)) {
        remoteEntities = profileData.user_following_entities;
      }
      if (Array.isArray(profileData.publishers)) {
        existingPublishers = profileData.publishers;
      }
      existingSourceBrand = profileData.source_brand || null;
    }
  } catch (e) {
    console.warn("[PIG] Could not fetch klaviyo-profile for merge:", e);
  }

  const merged = [...remoteEntities];

  const entityInList = (list, id) => {
    return list.some(e => (e.id || e) === id);
  };

  if (entity && entity.id) {
    if (!entityInList(merged, entity.id)) {
      const newEntity = {
        id: entity.id,
        name: entity.name,
        name_for_digest: entity.name_for_digest || entity.name,
        entity_type: entity.entityType,
      };
      if (
        (entity.entityType === "player" || entity.entityType === "manager") &&
        entity.currentClubName
      ) {
        newEntity.club_name_for_digest = entity.currentClubName;
      }
      merged.push(newEntity);
    }
  }

  const publishersList = [...existingPublishers];
  const alreadyPublisher = publishersList.some(p => p === PUBLISHER_NAME);
  if (!alreadyPublisher) {
    publishersList.push(PUBLISHER_NAME);
  }

  const attrs = { email: email };
  const props = {};
  if (merged.length) props.user_following_entities = merged;
  if (!alreadyPublisher) props.publishers = publishersList;
  if (!existingSourceBrand || PUBLISHER_NAME === "Football Insider") {
    props.source_brand = PUBLISHER_NAME;
  }
  if (Object.keys(props).length) {
    attrs.properties = props;
  }

  const makeCall = (listId) => {
    const payload = {
      data: {
        type: "subscription",
        attributes: {
          custom_source: `${entity?.entityType ? entity.entityType.charAt(0).toUpperCase() + entity.entityType.slice(1) : "Entity"} Identity Gate`,
          profile: { data: { type: "profile", attributes: attrs } },
        },
        relationships: { list: { data: { type: "list", id: listId } } },
      },
    };
    return fetch(
      `https://a.klaviyo.com/client/subscriptions/?company_id=${KLAVIYO_PUBLIC_KEY}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          revision: "2023-12-15",
        },
        body: JSON.stringify(payload),
      }
    ).then(res => {
      if (!res.ok) {
        return res.json().then(d => {
          throw new Error("Klaviyo " + res.status + ": " + JSON.stringify(d));
        });
      }
    });
  };

  let klaviyoLists = [];
  try {
    let res = await fetch("/api/klaviyo-lists");
    if (!res.ok || res.status === 404) {
      res = await fetch("https://www.footballinsider247.com/api/klaviyo-lists");
    }
    if (res.ok) {
      const data = await res.json();
      if (data.status === "ok") {
        klaviyoLists = data.lists || [];
      }
    }
  } catch (e) {
    console.warn("[PIG] Could not fetch Klaviyo lists:", e);
  }

  const getClubListId = (ent) => {
    let clubName = null;
    if (
      ent &&
      (ent.entityType === "player" || ent.entityType === "manager") &&
      ent.currentClubName
    ) {
      clubName = ent.currentClubName
        .replace(/\s*Football Club\s*$/i, "")
        .trim();
    } else if (ent && ent.entityType === "club" && ent.name) {
      clubName = ent.name.replace(/\s*Football Club\s*$/i, "").trim();
    }
    if (!clubName) return null;
    const targetName = clubName + " Subscribers";
    const match = klaviyoLists.find(l =>
      l.attributes &&
      l.attributes.name &&
      l.attributes.name.toLowerCase() === targetName.toLowerCase()
    );
    return match && match.id ? match.id : null;
  };

  const calls = [makeCall(KLAVIYO_LIST_ID)];
  const clubListId = getClubListId(entity);
  if (clubListId) {
    calls.push(makeCall(clubListId));
  }

  return Promise.all(calls).catch(err => {
    console.error("[PIG] Klaviyo error:", err);
    throw err;
  });
}

